/**
 * Fixture Texture Region Utilities
 *
 * Functions for managing wall texture regions associated with fixture groups
 * (showers, bathtubs, sinks/vanities). Handles cleanup on deletion and
 * recalculation on resize.
 */

import {
  resplitWainscotWalls,
  snapshotWallRegions,
} from '@shared-lib/estimate-pricing/designCenter/wallTreatment';
import { isObjectType } from './objectTypeUtils';
import { isSink } from '@shared-lib/sink-types';
import { isGeneratedKitchenBacksplash } from '@shared-lib/kitchen-assembly/styling/kitchenBacksplash';
import {
  findAllShowerWallIds,
  computeShowerWallRegion,
} from './showerWallHelpers';
import {
  findAllTubWallIds,
  computeTubBacksplashRegion,
} from './bathtubSurroundHelpers';
import { belongsToWall, getParentWallIds } from './parentWallHelpers';
import { vanitySplashOff, tubBandOff } from './bathroomBandHeights';
import {
  findWallsNearObjects,
  computeBacksplashRegionPure,
  vanityExtentObjects,
} from './vanitySplashGeometry';
import { isShowerFloorRegion } from '@shared-lib/estimate-pricing/utils/showerFloorRegion';
import { showerTileRegionOptions } from '@shared-lib/geometry/showerWallTiling';

/**
 * Determine the fixture type for texture region operations.
 * @param {Object} obj - Room object
 * @returns {'shower'|'bathtub'|'backsplash'|null}
 */
export function getFixtureRegionType(obj) {
  if (!obj) return null;
  if (isObjectType(obj, 'shower')) return 'shower';
  if (isObjectType(obj, 'bathtub')) return 'bathtub';
  // Sink/vanity backsplash: countertops, lower-cabinets, ranges (stove),
  // wall ovens, sinks, vanities. Both old (stove / oven) and new (range /
  // wall-oven) names accepted — saved structures from before the GLB-
  // aligned type rename flow through this filter too.
  if (
    isObjectType(obj, 'countertop') ||
    isObjectType(obj, 'lower-cabinet') ||
    isObjectType(obj, 'stove') ||
    isObjectType(obj, 'range') ||
    isObjectType(obj, 'cooktop') ||
    isObjectType(obj, 'oven') ||
    isObjectType(obj, 'wall-oven') ||
    isSink(obj) ||
    isObjectType(obj, 'vanity')
  ) {
    return 'backsplash';
  }
  return null;
}

/**
 * Check if a wall texture region matches a fixture for removal.
 * @param {Object} region - Wall texture region
 * @param {string} fixtureType - 'shower'|'bathtub'|'backsplash'
 * @param {string} fixtureGroupId - fixtureGroupId of the fixture being removed
 * @returns {boolean} True if region should be removed
 */
function regionMatchesFixture(region, fixtureType, fixtureGroupId) {
  if (fixtureType === 'shower') {
    // Match by source field (primary) or surfaceType
    if (region.source === 'shower') {
      // If we have a fixtureGroupId, also verify it matches
      if (fixtureGroupId && region.fixtureGroupId) {
        return region.fixtureGroupId === fixtureGroupId;
      }
      return true;
    }
    if (region.surfaceType === 'showerWall') {
      if (fixtureGroupId && region.fixtureGroupId) {
        return region.fixtureGroupId === fixtureGroupId;
      }
      return true;
    }
    return false;
  }

  if (fixtureType === 'bathtub') {
    if (region.surfaceType === 'tubBacksplash') {
      if (fixtureGroupId && region.fixtureGroupId) {
        return region.fixtureGroupId === fixtureGroupId;
      }
      return true;
    }
    return false;
  }

  if (fixtureType === 'backsplash') {
    if (region.surfaceType === 'backsplash') {
      if (fixtureGroupId && region.fixtureGroupId) {
        return region.fixtureGroupId === fixtureGroupId;
      }
      // Fall back to groupId matching
      if (fixtureGroupId && region.groupId) {
        return region.groupId === fixtureGroupId;
      }
      return true;
    }
    return false;
  }

  return false;
}

/**
 * The wainscot always meets the shower tile (Yu-Han, 2026-10-22): after a
 * recalculator has changed `room`'s shower tile IN PLACE, re-split the
 * wainscot beside it with the shared rule (`resplitWainscotWalls`), in place
 * too — the recalculators' contract — against the walls as they were
 * (`before`, a `snapshotWallRegions` taken first). Every caller gets it: the
 * store sync, and the 3D delete, the floor plan's delete / drag / resize and
 * the model swap, which call these directly.
 */
function resplitWainscotInPlace(room, before) {
  if (!Array.isArray(room?.walls)) return;
  const resplit = resplitWainscotWalls(room.walls, before);
  if (resplit === room.walls) return;
  resplit.forEach((wall, i) => {
    if (wall !== room.walls[i]) room.walls[i].texture = wall.texture;
  });
}

/**
 * Remove wall and floor texture regions associated with a fixture object.
 * Iterates all walls and floors in the room and filters out matching regions.
 *
 * @param {Object} fixtureObject - The fixture being deleted (shower, bathtub, etc.)
 * @param {Object} room - Room data with .walls and .floors arrays
 * @returns {Object} Updated room with regions removed (mutates in place)
 */
export function removeFixtureRegions(fixtureObject, room) {
  if (!fixtureObject) return room;

  const fixtureType = getFixtureRegionType(fixtureObject);
  if (!fixtureType) return room;
  const wallsBefore = snapshotWallRegions(room.walls);

  const groupId = fixtureObject.fixtureGroupId || null;

  // Remove matching regions from walls
  if (room.walls) {
    for (const wall of room.walls) {
      if (!wall.texture?.regions || !Array.isArray(wall.texture.regions)) {
        continue;
      }
      wall.texture.regions = wall.texture.regions.filter(
        (region) => !regionMatchesFixture(region, fixtureType, groupId)
      );
    }
  }

  // Remove matching regions from floors (e.g., shower floor regions)
  if (room.floors && fixtureType === 'shower') {
    for (const floor of room.floors) {
      if (!floor.texture?.regions || !Array.isArray(floor.texture.regions)) {
        continue;
      }
      floor.texture.regions = floor.texture.regions.filter((region) => {
        // Match by fixtureGroupId if available
        if (groupId && region.fixtureGroupId === groupId) return false;
        // Match by surfaceType
        if (region.surfaceType === 'showerFloor') {
          if (groupId && region.fixtureGroupId) {
            return region.fixtureGroupId !== groupId;
          }
          return false;
        }
        // Match by label
        if (region.label === 'Shower Floor') {
          if (groupId && region.fixtureGroupId) {
            return region.fixtureGroupId !== groupId;
          }
          return false;
        }
        // Match by regionId pattern
        if (
          region.regionId?.startsWith('region_shower_') ||
          region.regionId?.startsWith('showerfloor_')
        ) {
          if (groupId && region.fixtureGroupId) {
            return region.fixtureGroupId !== groupId;
          }
          return false;
        }
        return true;
      });
    }
  }

  resplitWainscotInPlace(room, wallsBefore);
  return room;
}

/**
 * Remove wall texture regions for ALL objects being deleted.
 * Convenience wrapper that handles checking each object for fixture type.
 *
 * @param {Array} objectsToDelete - Array of object IDs being deleted
 * @param {Object} room - Room data with .walls and .objects arrays
 * @returns {Object} Updated room with regions removed
 */
export function removeFixtureRegionsForDeletedObjects(objectsToDelete, room) {
  if (!objectsToDelete?.length || !room?.objects) return room;

  for (const objId of objectsToDelete) {
    const obj = room.objects.find((o) => o.id === objId);
    if (obj) {
      removeFixtureRegions(obj, room);
    }
  }

  return room;
}

/**
 * Remove ALL fixture-related texture regions from walls and floors.
 * Used when applying a new layout to clear stale regions.
 *
 * @param {Object} room - Room data with .walls and .floors arrays
 * @returns {Object} Updated room with all fixture regions removed
 */
export function removeAllFixtureRegions(room) {
  if (!room) return room;

  // Surface types that indicate fixture-generated regions
  const fixtureSurfaceTypes = new Set([
    'showerWall',
    'tubBacksplash',
    'backsplash',
  ]);
  const fixtureSources = new Set(['shower']);

  // Clean walls
  if (room.walls) {
    for (const wall of room.walls) {
      if (!wall.texture?.regions || !Array.isArray(wall.texture.regions)) {
        continue;
      }
      wall.texture.regions = wall.texture.regions.filter(
        (region) =>
          !fixtureSurfaceTypes.has(region.surfaceType) &&
          !fixtureSources.has(region.source)
      );
    }
  }

  // Clean floors (shower floor regions)
  if (room.floors) {
    for (const floor of room.floors) {
      if (!floor.texture?.regions || !Array.isArray(floor.texture.regions)) {
        continue;
      }
      floor.texture.regions = floor.texture.regions.filter((region) => {
        // Match shower floor regions by any identifier
        if (region.surfaceType === 'showerFloor') return false;
        if (region.label === 'Shower Floor') return false;
        if (region.regionId?.startsWith('region_shower_')) return false;
        if (region.regionId?.startsWith('showerfloor_')) return false;
        if (region.fixtureGroupId?.includes('shower_group_')) return false;
        if (region.fixtureGroupId?.includes('shower')) return false;
        return true;
      });
    }
  }

  return room;
}

/**
 * Recalculate wall texture regions after a fixture has been resized.
 * Uses existing geometry helpers to recompute region bounds.
 *
 * Only handles showers and bathtubs (sink backsplash requires Three.js context).
 *
 * @param {Object} fixtureObject - The resized fixture (with NEW dimensions/center)
 * @param {Object} room - Room data with .walls and .objects arrays
 * @returns {Object} Updated room with recalculated regions
 */
export function recalculateFixtureRegions(fixtureObject, room, options = {}) {
  if (!fixtureObject || !room?.walls) {
    return room;
  }

  const fixtureType = getFixtureRegionType(fixtureObject);
  if (!fixtureType) {
    return room;
  }

  if (fixtureType === 'shower') {
    // the shower's tile moves: the wainscot follows it
    const wallsBefore = snapshotWallRegions(room.walls);
    const next = recalculateShowerRegions(fixtureObject, room);
    resplitWainscotInPlace(next, wallsBefore);
    return next;
  }

  if (fixtureType === 'backsplash') {
    return recalculateBacksplashRegions(fixtureObject, room);
  }

  if (fixtureType === 'bathtub') {
    return recalculateTubRegions(fixtureObject, room, options);
  }

  return room;
}

// One definition, shared with the estimate (shared-lib/estimate-pricing).
export { isShowerFloorRegion };

/**
 * Calculate shower floor region on a floor surface.
 * Replicates the logic from cloud functions' calculateShowerFloorRegion.
 * @param {Object} shower - Shower object with center, dimensions, rotation
 * @param {Object} floor - Floor object with center, dimensions, polygonCorners
 * @returns {Object|null} Region {x, y, width, height} in percentages 0-100
 */
export function calculateShowerFloorRegion(shower, floor) {
  if (
    !shower?.center ||
    !floor?.center ||
    !shower?.dimensions ||
    !floor?.dimensions
  ) {
    return null;
  }

  let floorWidth, floorDepth, floorMinX, floorMinZ;

  if (floor.polygonCorners && floor.polygonCorners.length > 0) {
    let minX = Infinity,
      maxX = -Infinity,
      minZ = Infinity,
      maxZ = -Infinity;
    floor.polygonCorners.forEach((corner) => {
      minX = Math.min(minX, corner.x);
      maxX = Math.max(maxX, corner.x);
      minZ = Math.min(minZ, corner.z);
      maxZ = Math.max(maxZ, corner.z);
    });
    floorWidth = maxX - minX;
    floorDepth = maxZ - minZ;
    floorMinX = minX;
    floorMinZ = minZ;
  } else {
    floorWidth = floor.dimensions.x || floor.dimensions.width || 3;
    floorDepth = floor.dimensions.z || floor.dimensions.depth || 3;
    floorMinX = floor.center.x - floorWidth / 2;
    floorMinZ = floor.center.z - floorDepth / 2;
  }

  let showerWidth = shower.dimensions.x;
  let showerDepth = shower.dimensions.z;

  if (shower.rotation?.y !== undefined) {
    const rotDeg = ((shower.rotation.y * 180) / Math.PI) % 360;
    const normRot = rotDeg < 0 ? rotDeg + 360 : rotDeg;
    const isRotated90or270 =
      (normRot > 45 && normRot < 135) || (normRot > 225 && normRot < 315);
    if (isRotated90or270) {
      [showerWidth, showerDepth] = [showerDepth, showerWidth];
    }
  }

  const deltaX = shower.center.x - floorMinX;
  const deltaZ = shower.center.z - floorMinZ;
  const centerXPercent = (deltaX / floorWidth) * 100;
  const centerZPercent = (deltaZ / floorDepth) * 100;
  const widthPercent = (showerWidth / floorWidth) * 100;
  const depthPercent = (showerDepth / floorDepth) * 100;

  return {
    x: Math.max(0, Math.min(100, centerXPercent - widthPercent / 2)),
    y: Math.max(0, Math.min(100, centerZPercent - depthPercent / 2)),
    width: Math.min(100, widthPercent),
    height: Math.min(100, depthPercent),
  };
}

/**
 * Recalculate shower wall tiling regions and floor regions after shower resize.
 */
function recalculateShowerRegions(shower, room) {
  const wallIds = findAllShowerWallIds(shower, room);
  const groupId = shower.fixtureGroupId;

  // Capture texture data from existing shower regions before any removal,
  // so we can carry it over to newly created regions on different walls.
  let savedTextureData = null;
  for (const wall of room.walls) {
    if (!wall.texture?.regions) continue;
    for (const region of wall.texture.regions) {
      if (
        (region.source === 'shower' || region.surfaceType === 'showerWall') &&
        (!groupId ||
          !region.fixtureGroupId ||
          region.fixtureGroupId === groupId)
      ) {
        if (!savedTextureData && region.id) {
          const { x, y, width, height, ...rest } = region;
          savedTextureData = rest;
        }
        break;
      }
    }
    if (savedTextureData) break;
  }

  // The height the user chose for this shower's tile rides its regions
  // (`tileHeight`); a resize or a move re-tiles at it. A region from before
  // the choice existed reads as the default, Standard.
  const tileHeight = showerTileRegionOptions(
    savedTextureData?.tileHeight
  ).height;

  for (const wallId of wallIds) {
    const wall = room.walls.find((w) => w.id === wallId);
    if (!wall) continue;

    const newRegion = computeShowerWallRegion(shower, wallId, room, tileHeight);
    if (!newRegion) continue;

    // Ensure wall has texture and regions
    if (!wall.texture) wall.texture = {};
    if (!wall.texture.regions) wall.texture.regions = [];

    // Update existing shower regions on this wall
    let foundExisting = false;
    for (const region of wall.texture.regions) {
      if (region.source === 'shower' || region.surfaceType === 'showerWall') {
        region.x = newRegion.x;
        region.y = newRegion.y;
        region.width = newRegion.width;
        region.height = newRegion.height;
        region.tileHeight = tileHeight;
        foundExisting = true;
      }
    }

    // If no existing region, create a new one — carry over saved texture data
    if (!foundExisting) {
      wall.texture.regions.push({
        ...newRegion,
        surfaceType: 'showerWall',
        type: 'texture',
        source: 'shower',
        fixtureGroupId: groupId,
        ...(savedTextureData || {}),
        tileHeight,
      });
    }
  }

  // Remove regions from walls the shower no longer touches
  for (const wall of room.walls) {
    if (wallIds.includes(wall.id)) continue;
    if (!wall.texture?.regions) continue;

    wall.texture.regions = wall.texture.regions.filter((region) => {
      if (region.source === 'shower' || region.surfaceType === 'showerWall') {
        if (groupId && region.fixtureGroupId) {
          return region.fixtureGroupId !== groupId;
        }
        return false;
      }
      return true;
    });
  }

  // Recalculate shower floor regions
  if (room.floors && room.floors.length > 0) {
    const floor = room.floors[0];
    const newFloorRegion = calculateShowerFloorRegion(shower, floor);

    if (newFloorRegion && floor.texture?.regions) {
      for (const region of floor.texture.regions) {
        // Match by fixtureGroupId, regionId pattern, or label
        const isShowerFloor =
          (groupId && region.fixtureGroupId === groupId) ||
          isShowerFloorRegion(region);
        if (isShowerFloor) {
          region.x = newFloorRegion.x;
          region.y = newFloorRegion.y;
          region.width = newFloorRegion.width;
          region.height = newFloorRegion.height;
        }
      }
    }
  }

  return room;
}

/**
 * Recalculate tub backsplash regions after bathtub resize.
 */
/**
 * The walls a tub's backsplash stands on: the walls its footprint lines up
 * with, and its parent walls. Also the `allTubWallIds` the band's corner
 * rules read (`computeTubBacksplashRegion`).
 */
export function tubWallIdsFor(bathtub, room) {
  const wallIds = findAllTubWallIds(bathtub, room);
  for (const pw of getParentWallIds(bathtub)) {
    if (!wallIds.includes(pw)) wallIds.push(pw);
  }
  return wallIds;
}

function recalculateTubRegions(bathtub, room, options = {}) {
  const wallIds = tubWallIdsFor(bathtub, room);

  const groupId = bathtub.fixtureGroupId;

  // Preserve walls that already have tubBacksplash regions for this tub.
  // Only during model swaps (preserveExistingWalls: true), where geometric
  // detection may fail for the new model. During moves, let walls drop
  // naturally when the tub moves away from them.
  let savedTextureData = null;
  for (const wall of room.walls) {
    if (!wall.texture?.regions) continue;
    for (const region of wall.texture.regions) {
      if (
        region.surfaceType === 'tubBacksplash' &&
        (!groupId ||
          !region.fixtureGroupId ||
          region.fixtureGroupId === groupId)
      ) {
        if (options.preserveExistingWalls && !wallIds.includes(wall.id)) {
          wallIds.push(wall.id);
        }
        if (!savedTextureData && region.id) {
          const { x, y, width, height, ...rest } = region;
          savedTextureData = rest;
        }
        break;
      }
    }
  }

  // A wall whose setting turned the band off keeps it off. It still counts
  // as a tub wall for the corner rules the other walls' bands follow.
  const offWallIds = new Set(
    wallIds.filter((id) => tubBandOff(room.walls.find((w) => w.id === id)))
  );

  for (const wallId of wallIds) {
    const wall = room.walls.find((w) => w.id === wallId);
    if (!wall || offWallIds.has(wallId)) continue;

    const newRegion = computeTubBacksplashRegion(
      bathtub,
      wallId,
      room,
      wallIds
    );
    if (!newRegion) continue;

    // Ensure wall has texture and regions
    if (!wall.texture) wall.texture = {};
    if (!wall.texture.regions) wall.texture.regions = [];

    // Update existing tub backsplash regions on this wall
    let foundExisting = false;
    for (const region of wall.texture.regions) {
      if (region.surfaceType === 'tubBacksplash') {
        region.x = newRegion.x;
        region.y = newRegion.y;
        region.width = newRegion.width;
        region.height = newRegion.height;
        foundExisting = true;
      }
    }

    // If no existing region, create a new one — carry over saved texture data
    if (!foundExisting) {
      wall.texture.regions.push({
        ...newRegion,
        surfaceType: 'tubBacksplash',
        type: 'texture',
        source: 'bathtub',
        fixtureGroupId: groupId,
        ...(savedTextureData || {}),
      });
    }
  }

  // Remove regions from walls the tub no longer touches, or whose setting
  // turned the band off
  const removedWallIds = [];
  for (const wall of room.walls) {
    if (wallIds.includes(wall.id) && !offWallIds.has(wall.id)) continue;
    if (!wall.texture?.regions) continue;

    const before = wall.texture.regions.length;
    wall.texture.regions = wall.texture.regions.filter((region) => {
      if (region.surfaceType === 'tubBacksplash') {
        if (groupId && region.fixtureGroupId) {
          return region.fixtureGroupId !== groupId;
        }
        return false;
      }
      return true;
    });
    if (wall.texture.regions.length < before) {
      removedWallIds.push(wall.id);
    }
  }

  // Notify scene to remove stale tile meshes for walls that lost backsplash
  if (removedWallIds.length > 0 && typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('backsplashRemoved', {
        detail: { wallIds: removedWallIds },
      })
    );
  }

  return room;
}

// ─── Sink / Vanity backsplash recalculation ────────────────────────

// The splash's geometry lives in `vanitySplashGeometry`; re-exported here,
// where callers have always found it.
export {
  computeBacksplashRegionPure,
  mirrorOverVanitySplash,
  vanityExtentObjects,
} from './vanitySplashGeometry';

/**
 * Recalculate sink/vanity backsplash regions after a countertop or
 * lower-cabinet has been moved.
 *
 * Uses pure geometry (no Three.js mesh required). Finds walls near the
 * fixture's objects, computes region bounds via wall projection, and
 * updates/creates/removes backsplash regions accordingly.
 *
 * **It owns the 4" VANITY splash and nothing else.** Every region test below
 * skips `isGeneratedKitchenBacksplash` — the same predicate the viewer uses to
 * withhold drag handles from those bands, and for the same reason: they are
 * derived from the cabinetry by `buildBacksplashRegions`, not hand-drawn, and
 * you re-tile them rather than reshape them.
 *
 * Matching on `surfaceType === 'backsplash'` alone is not enough, and the way
 * it failed is worth keeping: `isBacksplashExtent` includes ranges and lower
 * cabinets, so this runs in a KITCHEN too. It would find the kitchen's
 * generated band, adopt its `groupId`, and then overwrite `x/y/width/height`
 * in place with `computeBacksplashRegionPure`'s 4" rectangle — flattening a
 * band that ran up to the uppers into a vanity splash. Then it stuck: 4" is
 * under the 10.5" cut in `bandIsLowProfile`, so the store's resync read the
 * squashed band as a deliberate low-profile design and rebuilt the whole
 * kitchen at 3" from then on.
 *
 * It needed a mover to fire, which is why it surfaced when design packages
 * gained kitchen fixture slots: applying one swaps the range, fridge and
 * dishwasher, and each of those moves is exactly the trigger.
 */
function recalculateBacksplashRegions(fixtureObject, room) {
  const extentObjects = vanityExtentObjects(fixtureObject, room);
  if (extentObjects.length === 0) {
    return room;
  }

  // Find existing backsplash groupId by scanning for backsplash regions
  // on walls the fixture was previously on (via parentWallIds)
  let backsplashGroupId = null;
  let savedTextureData = null;

  const oldParentWallIds = new Set();
  for (const obj of extentObjects) {
    for (const wid of getParentWallIds(obj)) {
      oldParentWallIds.add(wid);
    }
  }
  for (const wall of room.walls) {
    if (!wall.texture?.regions) continue;
    for (const region of wall.texture.regions) {
      if (region.surfaceType !== 'backsplash') continue;
      if (isGeneratedKitchenBacksplash(region)) continue;
      if (oldParentWallIds.has(wall.id) || backsplashGroupId) {
        if (!backsplashGroupId && region.groupId) {
          backsplashGroupId = region.groupId;
        }
        if (
          !savedTextureData &&
          region.id &&
          (backsplashGroupId
            ? region.groupId === backsplashGroupId
            : oldParentWallIds.has(wall.id))
        ) {
          const { x, y, width, height, ...rest } = region;
          savedTextureData = rest;
        }
      }
    }
  }

  // Also try matching by fixtureGroupId
  if (!backsplashGroupId && fixtureObject.fixtureGroupId) {
    for (const wall of room.walls) {
      if (!wall.texture?.regions) continue;
      for (const region of wall.texture.regions) {
        if (
          region.surfaceType === 'backsplash' &&
          !isGeneratedKitchenBacksplash(region) &&
          region.fixtureGroupId === fixtureObject.fixtureGroupId
        ) {
          backsplashGroupId = region.groupId || region.fixtureGroupId;
          if (!savedTextureData && region.id) {
            const { x, y, width, height, ...rest } = region;
            savedTextureData = rest;
          }
          break;
        }
      }
      if (backsplashGroupId) break;
    }
  }

  // If no existing backsplash regions found, nothing to recalculate
  if (!backsplashGroupId && !savedTextureData) {
    return room;
  }

  // Find which walls the fixture objects are near now (geometry-based)
  const nearbyWallIds = findWallsNearObjects(extentObjects, room);

  // A wall whose setting turned the splash off keeps it off.
  const offWallIds = new Set(
    nearbyWallIds.filter((id) =>
      vanitySplashOff(room.walls.find((w) => w.id === id))
    )
  );

  // For each nearby wall, compute the backsplash region
  for (const wallId of nearbyWallIds) {
    const wall = room.walls.find((w) => w.id === wallId);
    if (!wall || offWallIds.has(wallId)) continue;

    const newRegion = computeBacksplashRegionPure(extentObjects, wallId, room);
    if (!newRegion) continue;

    if (!wall.texture) wall.texture = {};
    if (!wall.texture.regions) wall.texture.regions = [];

    // Update existing backsplash region on this wall (matching groupId)
    let foundExisting = false;
    for (const region of wall.texture.regions) {
      if (
        region.surfaceType === 'backsplash' &&
        !isGeneratedKitchenBacksplash(region) &&
        backsplashGroupId &&
        (region.groupId === backsplashGroupId ||
          region.fixtureGroupId === backsplashGroupId)
      ) {
        region.x = newRegion.x;
        region.y = newRegion.y;
        region.width = newRegion.width;
        region.height = newRegion.height;
        foundExisting = true;
      }
    }

    // If no existing region on this wall, create one with carried-over texture
    if (!foundExisting) {
      wall.texture.regions.push({
        ...newRegion,
        surfaceType: 'backsplash',
        type: 'texture',
        ...(savedTextureData || {}),
        ...(backsplashGroupId ? { groupId: backsplashGroupId } : {}),
      });
    }
  }

  // Remove backsplash regions from walls the fixture no longer touches, or
  // whose setting turned the splash off
  for (const wall of room.walls) {
    if (nearbyWallIds.includes(wall.id) && !offWallIds.has(wall.id)) continue;
    if (!wall.texture?.regions) continue;

    wall.texture.regions = wall.texture.regions.filter((region) => {
      if (region.surfaceType !== 'backsplash') return true;
      if (isGeneratedKitchenBacksplash(region)) return true;
      if (
        backsplashGroupId &&
        (region.groupId === backsplashGroupId ||
          region.fixtureGroupId === backsplashGroupId)
      ) {
        return false; // remove — fixture no longer near this wall
      }
      return true;
    });
  }

  return room;
}
